import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { useSnackbar } from 'notistack';
import { CompaniesService } from 'api';
import {
  COMPANY_MUTUAL_FUND_MARKS_DEFAULT_ERROR_MESSAGE,
  COMPANY_MUTUAL_FUND_MARKS_QUERY_KEY,
} from 'common/constants/services/companies';

export interface MutualFundMark {
  fund_name: string;
  fund_family: string;
  reporting_date: string;
  price_per_share: string | null;
  fair_value_usd: string | null;
  shares_held: number | null;
  filing_url: string;
}

export interface MutualFundMarksResponse {
  fund_families: string[];
  marks: MutualFundMark[];
}

const getCompanyMutualFundMarks = async (companyId: number): Promise<MutualFundMarksResponse> =>
  CompaniesService.apiCompaniesMutualFundMarksRead(companyId) as Promise<MutualFundMarksResponse>;

const useGetCompanyMutualFundMarks = (companyId: number | undefined) => {
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, isError } = useQuery({
    enabled: !isNil(companyId),
    queryFn: () => getCompanyMutualFundMarks(companyId as number),
    queryKey: [COMPANY_MUTUAL_FUND_MARKS_QUERY_KEY, companyId],
    retry: false,
  });

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(COMPANY_MUTUAL_FUND_MARKS_DEFAULT_ERROR_MESSAGE, { variant: 'error' });
    }
  }, [isError, enqueueSnackbar]);

  return { data, isLoading, isError };
};

export default useGetCompanyMutualFundMarks;
